import {
  AssumptionSetAssumptionProps,
  AssumptionSetDiffBuilder,
  AssumptionSetDiffWithProps,
  ChangedAssumptionSetAssumptionProps,
  ChangedRequirementProps,
  RequirementProps,
} from './assumptionSetDiffBuilder'
import { repeatTabs } from './textDiffFormatting'
import { VariableDiffBuilder } from './variableDiffBuilder'
import {
  ChangedArrowCommentProps,
  ChangedGranularityProps,
  ChangedIntervalsProps,
  ChangedStatesProps,
  ChangedTagsProps,
  ChangedTransitionFunctionsProps,
  TextFieldChangeProps,
  VariableProps,
  VariablePropsWithDiff,
} from './variableDiffTypes'

export class NetworkDiff {
  private title: string
  private variableDiffBuilder = new VariableDiffBuilder()
  private assumptionSetDiffBuilder = new AssumptionSetDiffBuilder()
  private defaultTransitionBandChange: { from: number; to: number } | null =
    null

  constructor({
    oldNetworkName,
    newNetworkName,
  }: {
    oldNetworkName: string
    newNetworkName: string
  }) {
    this.title = `Changes from "${oldNetworkName}" to "${newNetworkName}"`
  }

  addDeletedVariable(props: VariableProps): void {
    this.variableDiffBuilder.addDeletedVariable(props)
  }

  addAddedVariable(props: VariableProps): void {
    this.variableDiffBuilder.addAddedVariable(props)
  }

  addNewPublishedVariable(props: VariableProps): void {
    this.variableDiffBuilder.addNewPublishedVariable(props)
  }

  addUnpublishedVariable(props: VariableProps): void {
    this.variableDiffBuilder.addUnpublishedVariable(props)
  }

  addChangedName(props: VariablePropsWithDiff): void {
    this.variableDiffBuilder.addChangedName(props)
  }

  addChangedType(props: VariablePropsWithDiff): void {
    this.variableDiffBuilder.addChangedType(props)
  }

  addChangedIntervalUnit(props: VariablePropsWithDiff): void {
    this.variableDiffBuilder.addChangedIntervalUnit(props)
  }

  addChangedIntervals(props: ChangedIntervalsProps): void {
    this.variableDiffBuilder.addChangedIntervals(props)
  }

  addChangedTransitionFunctions(props: ChangedTransitionFunctionsProps): void {
    this.variableDiffBuilder.addChangedTransitionFunctions(props)
  }

  /**
   * The per-network default transition band (KBE-758). Reported once, at
   * network level, rather than folded into the variables that inherit it —
   * a one-line default change would otherwise print a change for every
   * inheriting semi-continuous variable in the release note.
   */
  setChangedDefaultTransitionBand(change: { from: number; to: number }): void {
    this.defaultTransitionBandChange = change
  }

  addChangedParameterizationType(props: VariablePropsWithDiff): void {
    this.variableDiffBuilder.addChangedParameterizationType(props)
  }

  addChangedStates(props: ChangedStatesProps): void {
    this.variableDiffBuilder.addChangedStates(props)
  }

  addChangedOptimalState(props: VariablePropsWithDiff): void {
    this.variableDiffBuilder.addChangedOptimalState(props)
  }

  addProbabilitiesChanged(props: VariableProps): void {
    this.variableDiffBuilder.addProbabilitiesChanged(props)
  }

  addChangedGranularity(props: ChangedGranularityProps): void {
    this.variableDiffBuilder.addChangedGranularity(props)
  }

  addChangedComment(props: TextFieldChangeProps): void {
    this.variableDiffBuilder.addChangedComment(props)
  }

  addChangedTags(props: ChangedTagsProps): void {
    this.variableDiffBuilder.addChangedTags(props)
  }

  addChangedDefinition(props: TextFieldChangeProps): void {
    this.variableDiffBuilder.addChangedDefinition(props)
  }

  addChangedReferences(props: TextFieldChangeProps): void {
    this.variableDiffBuilder.addChangedReferences(props)
  }

  addChangedFormula(props: TextFieldChangeProps): void {
    this.variableDiffBuilder.addChangedFormula(props)
  }

  addChangedPrevalenceExplanation(props: TextFieldChangeProps): void {
    this.variableDiffBuilder.addChangedPrevalenceExplanation(props)
  }

  addChangedArrowComment(props: ChangedArrowCommentProps): void {
    this.variableDiffBuilder.addChangedArrowComment(props)
  }

  addAddedAssumptionSet(assumptionSetName: string): void {
    this.assumptionSetDiffBuilder.addAddedAssumptionSet(assumptionSetName)
  }

  addDeletedAssumptionSet(assumptionSetName: string): void {
    this.assumptionSetDiffBuilder.addDeletedAssumptionSet(assumptionSetName)
  }

  addChangedAssumptionSetName(props: AssumptionSetDiffWithProps): void {
    this.assumptionSetDiffBuilder.addChangedAssumptionSetName(props)
  }

  addAddedAssumptionSetAssumption(props: AssumptionSetAssumptionProps): void {
    this.assumptionSetDiffBuilder.addAddedAssumptionSetAssumption(props)
  }

  addDeletedAssumptionSetAssumption(props: AssumptionSetAssumptionProps): void {
    this.assumptionSetDiffBuilder.addDeletedAssumptionSetAssumption(props)
  }

  addChangedAssumptionSetAssumption(
    props: ChangedAssumptionSetAssumptionProps,
  ): void {
    this.assumptionSetDiffBuilder.addChangedAssumptionSetAssumption(props)
  }

  addAddedRequirement(props: RequirementProps): void {
    this.assumptionSetDiffBuilder.addAddedRequirement(props)
  }

  addDeletedRequirement(props: RequirementProps): void {
    this.assumptionSetDiffBuilder.addDeletedRequirement(props)
  }

  addChangedRequirement(props: ChangedRequirementProps): void {
    this.assumptionSetDiffBuilder.addChangedRequirement(props)
  }

  // Rendered only when it changed, so a release note that doesn't touch the
  // network default stays byte-identical to what this tool produced before.
  private renderNetworkSettingChanges = (): string =>
    this.defaultTransitionBandChange
      ? `Changed network settings:
${repeatTabs(1)}Default transition band: ±${this.defaultTransitionBandChange.from}% -> ±${this.defaultTransitionBandChange.to}%\n\n`
      : ''

  getDiffText = (options?: {
    displayPublicationStatus?: boolean
    displayAssumptionSets?: boolean
  }) => `${this.title}\n\n
${this.renderNetworkSettingChanges()}Deleted variables:
${this.variableDiffBuilder.renderDeletedVariableList()}
\nAdded variables:
${this.variableDiffBuilder.renderAddedVariableList()}
\n${
    options?.displayPublicationStatus
      ? `Unpublished variables:
${this.variableDiffBuilder.renderUnpublishedVariableList()}\n
New published variables:
${this.variableDiffBuilder.renderNewPublishedVariableList()}\n`
      : ''
  }
Changed variables:
${this.variableDiffBuilder.renderVariableChanges()}\n
${
  options?.displayAssumptionSets
    ? `Deleted assumptionSets:
${this.assumptionSetDiffBuilder.renderDeletedAssumptionSetList()}\n
Added assumptionSets:
${this.assumptionSetDiffBuilder.renderAddedAssumptionSetList()}\n
Changed assumptionSets:
${this.assumptionSetDiffBuilder.renderAssumptionSetChanges()}\n`
    : ''
}
`
}
